import {createEntityMeta,SyncStatus} from '../offline/entity.js';
import {
  JournalNoteScope,
  createJournalNote,
  journalNoteEntityType
} from './journal-note.js';

export const EDITABLE_NOTE_SESSION_STATUSES=Object.freeze(['draft','planned','preparation','ready']);
const EDITABLE=new Set(EDITABLE_NOTE_SESSION_STATUSES);

function required(value,label){
  const text=String(value||'').trim();
  if(!text)throw new TypeError(label+' is required');
  return text;
}

function assertEditable(status){
  const value=String(status||'').trim().toLowerCase();
  if(!EDITABLE.has(value))throw new Error('JOURNAL_NOTE_LOCKED_AFTER_START');
}

function merge(existing,note,{now}){
  if(existing){
    const copy={...existing};
    delete copy.key;
    delete copy.entityType;
    return {...copy,...note,id:note.scopeId,userId:existing.userId,deletedAt:null};
  }
  return {
    ...createEntityMeta({id:note.scopeId,now,syncStatus:SyncStatus.PENDING_CREATE}),
    ...note,
    id:note.scopeId
  };
}

export function createJournalNoteStore({
  repository,
  queue,
  userId,
  now=()=>new Date().toISOString()
}={}){
  if(!repository?.get||!repository?.put)throw new TypeError('repository with get/put is required');
  if(!queue?.enqueue||!queue?.markConflict)throw new TypeError('sync queue is required');
  const owner=required(userId,'userId');

  async function get(scopeKind,scopeId){
    const type=journalNoteEntityType(scopeKind);
    const row=await repository.get(type,required(scopeId,'scopeId'));
    if(!row||row.deletedAt)return null;
    if(row.userId!==owner)throw new Error('JOURNAL_NOTE_OWNER_MISMATCH');
    return Object.freeze(row);
  }

  async function save(scopeKind,scopeId,text,{sessionStatus='draft'}={}){
    assertEditable(sessionStatus);
    const type=journalNoteEntityType(scopeKind);
    const id=required(scopeId,'scopeId');
    const existing=await repository.get(type,id);
    if(existing?.userId&&existing.userId!==owner)throw new Error('JOURNAL_NOTE_OWNER_MISMATCH');
    if(existing?.syncStatus===SyncStatus.CONFLICT)throw new Error('JOURNAL_NOTE_CONFLICT');
    const note=createJournalNote({scopeKind,scopeId:id,userId:owner,text});
    const entity=merge(existing,note,{now:now()});
    const operation=existing&&Number(existing.serverVersion)>0?'update':'create';
    return queue.enqueue(type,entity,operation);
  }

  async function clear(scopeKind,scopeId,{sessionStatus='draft'}={}){
    assertEditable(sessionStatus);
    const type=journalNoteEntityType(scopeKind);
    const id=required(scopeId,'scopeId');
    const existing=await repository.get(type,id);
    if(!existing)return null;
    if(existing.userId!==owner)throw new Error('JOURNAL_NOTE_OWNER_MISMATCH');
    if(existing.syncStatus===SyncStatus.CONFLICT)throw new Error('JOURNAL_NOTE_CONFLICT');
    return queue.enqueue(type,existing,'delete');
  }

  async function markConflict(scopeKind,scopeId,details){
    const type=journalNoteEntityType(scopeKind);
    const existing=await repository.get(type,required(scopeId,'scopeId'));
    if(!existing)throw new Error('JOURNAL_NOTE_NOT_FOUND');
    if(existing.userId!==owner)throw new Error('JOURNAL_NOTE_OWNER_MISMATCH');
    return queue.markConflict(type,existing,details);
  }

  return Object.freeze({
    get,
    save,
    clear,
    markConflict,
    getSession:sessionId=>get(JournalNoteScope.SESSION,sessionId),
    getItem:itemId=>get(JournalNoteScope.ITEM,itemId),
    saveSession:(sessionId,text,options)=>save(JournalNoteScope.SESSION,sessionId,text,options),
    saveItem:(itemId,text,options)=>save(JournalNoteScope.ITEM,itemId,text,options),
    clearSession:(sessionId,options)=>clear(JournalNoteScope.SESSION,sessionId,options),
    clearItem:(itemId,options)=>clear(JournalNoteScope.ITEM,itemId,options)
  });
}
